"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import {
  IconBrandDatabricks,
  IconHome,
  IconRosetteDiscountCheckFilled,
  IconUserBolt,
  IconMenu2,
  IconArrowUpRight,
  IconAutomation,
} from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const Navbar = () => {
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  const buttons = [
    { label: "Home", href: "/", icon: <IconHome size={22} /> },
    { label: "Services", href: "/services", icon: <IconAutomation size={22} /> },
    { label: "About", href: "/about", icon: <IconUserBolt size={22} /> },
    {
      label: "Projects",
      href: "/projects",
      icon: <IconBrandDatabricks size={22} />,
    },
  ];

  const isActive = (href: string) => {
    if (href.startsWith("/#")) return false;
    if (href === "/") return pathname === "/";
    // Treat nested routes under /about or /projects as active too
    return pathname?.startsWith(href);
  };

  return (
    <div
      className="sticky top-5 z-50 mx-auto text-white"
      style={{ maxWidth: "1440px" }}
    >
      <div className="rounded-[20px] border border-white/[0.06] bg-black/85 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.8)] backdrop-blur-xl" style={{ height: 80 }}>
        <div className="flex items-center justify-between h-full px-5">
          {/* Left: Logo + handle */}
          <div className="flex items-center">
            <Link href="/" className="no-underline">
              <div className="flex items-center cursor-pointer">
                <div className="relative h-10 w-10 mr-2.5 overflow-hidden rounded-full ring-1 ring-white/10">
                  <Image
                    src="/subhannadeem.png"
                    alt="@SubhanNadeem"
                    fill
                    sizes="40px"
                    quality={100}
                    priority
                    className="object-cover"
                  />
                </div>

                <div className="flex items-center">
                  <span className="font-bold text-white text-[16px] sm:text-[18px] xl:text-[24px]">
                    @SubhanNadeem
                  </span>
                  <IconRosetteDiscountCheckFilled
                    size={20}
                    className="ml-[5px] mt-[2px]"
                    style={{ color: "#48AEEF" }}
                  />
                </div>
              </div>
            </Link>
          </div>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1 font-bold">
            {buttons.map((button) => {
              const hovered = hoveredButton === button.label;
              const active = isActive(button.href);
              return (
                <Link
                  href={button.href}
                  key={button.label}
                  className="no-underline"
                >
                  <Button
                    variant="ghost"
                    className={`h-10 rounded-[8px] px-3 text-[16px] transition-colors xl:px-4 xl:text-[18px] ${
                      active || hovered
                        ? "text-white bg-[#0E1018]"
                        : "text-[#868E96] bg-transparent"
                    }`}
                    onMouseEnter={() => setHoveredButton(button.label)}
                    onMouseLeave={() => setHoveredButton(null)}
                  >
                    <div className="flex items-center">
                      <span className="hidden xl:inline-flex">{button.icon}</span>
                      <span className="xl:ml-[7px]">{button.label}</span>
                    </div>
                  </Button>
                </Link>
              );
            })}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:block">
            <Button
              asChild
              className="h-12 rounded-[10px] bg-white px-5 text-[17px] font-bold xl:h-[60px] xl:w-[160px] xl:text-[20px]"
            >
              <Link href="https://wa.me/923057474003">
                Let&apos;s chat
                <IconArrowUpRight
                  size={18}
                  aria-hidden="true"
                  className="transition-transform duration-200 group-hover:translate-x-0.5"
                />
              </Link>
            </Button>
          </div>

          {/* Mobile: Burger + Sheet */}
          <div className="lg:hidden">
            <Sheet open={opened} onOpenChange={setOpened}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-10 w-10 p-0 text-white"
                  onClick={() => setOpened(true)}
                  aria-label="Open menu"
                >
                  <IconMenu2 size={26} />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-[82%] max-w-[340px] gap-0 border-l border-white/10 bg-[#07080d] p-0 text-white sm:max-w-[340px]"
              >
                <SheetTitle className="sr-only">Navigation</SheetTitle>

                <div className="flex items-center gap-3 border-b border-white/[0.06] px-5 py-5">
                  <div className="relative h-11 w-11 overflow-hidden rounded-full ring-1 ring-white/10">
                    <Image src="/subhannadeem.png" alt="" fill sizes="44px" className="object-cover" />
                  </div>
                  <div className="min-w-0">
                    <p className="flex items-center gap-1 text-[16px] font-bold">
                      Subhan Nadeem
                      <IconRosetteDiscountCheckFilled size={16} style={{ color: "#48AEEF" }} />
                    </p>
                    <p className="text-[13px] text-[#868E96]">Senior Full-Stack Engineer</p>
                  </div>
                </div>

                <nav className="flex flex-col gap-1 px-3 py-4">
                  {buttons.map((button) => {
                    const active = isActive(button.href);
                    return (
                      <button
                        key={button.label}
                        type="button"
                        onClick={() => {
                          setOpened(false);
                          router.push(button.href);
                        }}
                        className={`flex h-12 w-full items-center gap-3 rounded-[12px] px-3 text-left text-[16px] font-semibold transition-colors ${
                          active ? "bg-white/[0.06] text-white" : "text-[#A5B0BC] hover:bg-white/[0.04] hover:text-white"
                        }`}
                      >
                        <span className={active ? "text-[#48AEEF]" : "text-[#5C6670]"}>{button.icon}</span>
                        {button.label}
                      </button>
                    );
                  })}
                </nav>

                <div className="mt-auto border-t border-white/[0.06] p-5">
                  <div className="mb-4 flex items-center gap-2 text-[13px] text-[#9AA3AD]">
                    <span className="h-2 w-2 rounded-full bg-emerald-400" />
                    Available for new projects
                  </div>
                  <Link
                    href="https://wa.me/923057474003"
                    target="_blank"
                    onClick={() => setOpened(false)}
                    className="flex h-12 w-full items-center justify-center gap-2 rounded-[12px] bg-white text-[16px] font-bold text-black"
                  >
                    Let&apos;s chat
                    <IconArrowUpRight size={18} aria-hidden="true" />
                  </Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navbar;
